import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthState, LoginCredentials } from '../../types';
import { buildApiUrl, STORAGE_TOKEN_KEY } from '../../services/apiClient';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials, codigoDinamico?: string) => Promise<{ ok: boolean; codigoRequerido?: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = 'tiempos_auth_user';
const DEVICE_ID_KEY = 'tiempos_auth_device_id';

const getOrCreateDeviceId = (): string => {
  const saved = localStorage.getItem(DEVICE_ID_KEY);
  if (saved && saved.trim()) return saved;

  const nextId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  localStorage.setItem(DEVICE_ID_KEY, nextId);
  return nextId;
};

const getDeviceName = (): string => {
  const ua = navigator.userAgent || 'dispositivo-desconocido';
  return ua.slice(0, 180);
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Cargar usuario de localStorage al iniciar y validar token
  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = localStorage.getItem(STORAGE_TOKEN_KEY);
        const stored = localStorage.getItem(STORAGE_KEY);

        if (token && stored) {
          // Validar token con el backend
          try {
            const response = await fetch(buildApiUrl('/auth/me'), {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            if (response.ok) {
              const user = await response.json();
              // Mapear campos del backend al formato frontend
              const mappedUser: User = {
                id: user.id,
                username: user.username,
                role: user.role,
                nombre: user.nombre,
                bancaId: user.banca_id,
                localId: user.local_id,
                esPropio: Boolean(user.es_propio),
                limiteApuestaMaxima: user.limite_apuesta_maxima,
              };
              localStorage.setItem(STORAGE_KEY, JSON.stringify(mappedUser));
              setState({ user: mappedUser, isAuthenticated: true, isLoading: false });
              return;
            }
          } catch {
            // Si falla la validacion, intentar con datos locales
          }

          // Fallback: usar datos almacenados si el backend no responde
          const user = JSON.parse(stored) as User;
          setState({ user, isAuthenticated: true, isLoading: false });
        } else {
          setState(prev => ({ ...prev, isLoading: false }));
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_TOKEN_KEY);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    loadUser();
  }, []);

  const login = async (credentials: LoginCredentials, codigoDinamico?: string): Promise<{ ok: boolean; codigoRequerido?: boolean; error?: string }> => {
    try {
      const deviceId = getOrCreateDeviceId();
      const response = await fetch(buildApiUrl('/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
          device_id: deviceId,
          device_name: getDeviceName(),
          codigo_dinamico: codigoDinamico || undefined,
        }),
      });

      if (!response.ok) {
        let body: any = null;
        try {
          body = await response.json();
        } catch {
          body = null;
        }

        const detail = body?.detail;
        if (detail && typeof detail === 'object' && detail.codigo_requerido === true) {
          return {
            ok: false,
            codigoRequerido: true,
            error: detail.message || 'Se requiere código dinámico para este dispositivo.',
          };
        }

        return {
          ok: false,
          error: typeof detail === 'string' ? detail : 'Usuario o contraseña incorrectos',
        };
      }

      const data = await response.json();
      localStorage.setItem(STORAGE_TOKEN_KEY, data.access_token);

      // Mapear campos del backend al formato frontend
      const user: User = {
        id: data.user.id,
        username: data.user.username,
        role: data.user.role,
        nombre: data.user.nombre,
        bancaId: data.user.banca_id,
        localId: data.user.local_id,
        esPropio: Boolean(data.user.es_propio),
        limiteApuestaMaxima: data.user.limite_apuesta_maxima,
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
      setState({ user, isAuthenticated: true, isLoading: false });
      return { ok: true };
    } catch {
      return { ok: false, error: 'Error al iniciar sesión' };
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem(STORAGE_TOKEN_KEY);
      if (token) {
        await fetch(buildApiUrl('/auth/logout'), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      }
    } catch {
      // Ignorar errores de red en logout
    }
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_TOKEN_KEY);
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
